import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { saveProfile, loadIdentityLock, clearIdentityLock, LOCKED_FIELDS } from "../profileStore";
import api from "../api";

const Onboarding = () => {
  const { t, lang } = useI18n();
  const { serviceMode } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sports, setSports] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Identity lock — read once on mount. If set, the locked fields (name, sex,
  // age, sport, position) are read-only so a single Pro access code can't be
  // re-used to onboard multiple different people.
  // SERVICE MODE (!iulserv@) bypass: owner code must NEVER be restricted, and
  // any stale lock left from a previous Pro session is purged on mount.
  // LIFESTYLE bypass: Healthy Living is always free — family members can
  // onboard with any name. When the user picks Lifestyle on "Plan nou", the
  // Pro lock does not apply (only re-applies if they pick Pro again).
  if (serviceMode && typeof window !== "undefined" && localStorage.getItem("sne_identity_lock")) {
    clearIdentityLock();
  }
  const rawLock = serviceMode ? null : loadIdentityLock();
  const [profile, setProfile] = useState({
    name: rawLock?.name || "",
    sex: rawLock?.sex || "M",
    profile_type: rawLock?.profile_type || "pro",
    age: rawLock?.age || 25,
    weight_kg: 75,
    height_cm: 180,
    sport: rawLock?.sport || "",
    position: rawLock?.position || "",
    activity_level: "intense",
    goal: "performance",
    special_conditions: [],
  });
  // The Pro lock only constrains the user while they stay on the Pro side.
  // The moment they switch to Lifestyle (Healthy Living), the lock is bypassed
  // so a family member can onboard with their own name/age.
  const identityLock = profile.profile_type === "pro" ? rawLock : null;
  const isLocked = (field) => !!identityLock && LOCKED_FIELDS.includes(field);

  useEffect(() => {
    api.get("/catalog/sports").then((r) => {
      setSports(r.data.sports);
      if (r.data.sports.length && !profile.sport) {
        // Default to first non-lifestyle sport
        const first = r.data.sports.find((s) => s.key !== "lifestyle") || r.data.sports[0];
        setProfile((p) => ({ ...p, sport: first.key, position: first.positions[0].key }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  // When user picks profile_type, switch sport list accordingly
  const onProfileTypeChange = (type) => {
    set("profile_type", type);
    if (type === "lifestyle") {
      const ls = sports.find((s) => s.key === "lifestyle");
      if (ls) {
        const firstPos = ls.positions[0].key;
        setProfile((p) => ({
          ...p,
          profile_type: "lifestyle",
          sport: "lifestyle",
          position: firstPos,
          activity_level: LIFESTYLE_POS_TO_ACTIVITY[firstPos] || "moderate",
          goal: "maintain",
        }));
      }
    } else {
      const first = sports.find((s) => s.key !== "lifestyle");
      if (first) {
        setProfile((p) => ({
          ...p,
          profile_type: "pro",
          sport: first.key,
          position: first.positions[0].key,
          activity_level: "intense",
          goal: "performance",
        }));
      }
    }
  };
  // Map lifestyle "position" (tier) -> backend activity_level key for TDEE factor.
  const LIFESTYLE_POS_TO_ACTIVITY = {
    sedentary: "sedentary",
    light_active: "light",
    moderately_active: "moderate",
    active: "intense",
    very_active: "performance",
  };
  const currentSport = sports.find((s) => s.key === profile.sport);
  const filteredSports = profile.profile_type === "lifestyle"
    ? sports.filter((s) => s.key === "lifestyle")
    : sports.filter((s) => s.key !== "lifestyle");
  const isLifestyle = profile.profile_type === "lifestyle";

  const finish = async () => {
    setError("");
    setBusy(true);
    try {
      // Consume one plan + persist the profile snapshot server-side so it can
      // be auto-restored after F5 / cookie wipe.
      await api.post("/plan/consume", {
        profile_type: profile.profile_type || "pro",
        profile,
      });
      saveProfile(profile, { skipLock: serviceMode });
      // replace: true so the browser Back button cannot return to the
      // onboarding wizard (which would let the user consume another plan).
      navigate("/app", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setError(t.dashboard.no_plans_left);
      } else {
        setError(e?.response?.data?.detail || "Could not generate plan");
      }
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    // Service mode (owner test code !iulserv@): bypass all Pro identity-lock
    // confirmations and name validation. Owner can iterate freely.
    if (profile.profile_type === "pro" && !serviceMode) {
      if (step === 1) {
        if (!profile.name || profile.name.trim().length < 2) {
          setError(t.onboarding.err_name);
          return;
        }
        const msg = t.onboarding.confirm_step1
          .replace("{name}", profile.name.trim())
          .replace("{age}", profile.age)
          .replace("{sex}", profile.sex === "M" ? t.onboarding.sex_m : t.onboarding.sex_f);
        if (!window.confirm(msg)) return;
      } else if (step === 2) {
        const sp = sports.find((s) => s.key === profile.sport);
        const pos = sp?.positions?.find((p) => p.key === profile.position);
        const msg = t.onboarding.confirm_step2
          .replace("{sport}", sp?.[`label_${lang}`] || profile.sport)
          .replace("{position}", pos?.[`label_${lang}`] || profile.position);
        if (!window.confirm(msg)) return;
      }
    }
    setError("");
    setStep((s) => Math.min(s + 1, 4));
  };
  // For PRO (non-service): Back hidden after step 2. Service mode keeps Back free.
  const canGoBack = step > 1 && !(profile.profile_type === "pro" && step >= 3 && !serviceMode);
  const back = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
      <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-3">
        // {t.onboarding.step} {step}/4
      </div>
      <h1 className="font-heading text-4xl md:text-5xl mb-8">{t.onboarding.title}</h1>

      <div className="sne-card">
        {step === 1 && (
          <div className="space-y-5">
            <Field label={t.onboarding.profile_type}>
              <div className="flex flex-col gap-2">
                <button
                  data-testid="onboard-profile-pro"
                  onClick={() => onProfileTypeChange("pro")}
                  className="py-3 font-mono text-xs tracking-widest text-left px-4"
                  style={{
                    background: profile.profile_type === "pro" ? "#FF3B30" : "transparent",
                    color: "white",
                    border: `1px solid ${profile.profile_type === "pro" ? "#FF3B30" : "#27272A"}`,
                    cursor: "pointer",
                  }}
                >
                  🏅 {t.onboarding.profile_pro}
                </button>
                <button
                  data-testid="onboard-profile-lifestyle"
                  onClick={() => onProfileTypeChange("lifestyle")}
                  className="py-3 font-mono text-xs tracking-widest text-left px-4"
                  style={{
                    background: profile.profile_type === "lifestyle" ? "#FF3B30" : "transparent",
                    color: "white",
                    border: `1px solid ${profile.profile_type === "lifestyle" ? "#FF3B30" : "#27272A"}`,
                    cursor: "pointer",
                  }}
                >
                  💪 {t.onboarding.profile_lifestyle}
                </button>
                {profile.profile_type === "lifestyle" && (
                  <div className="text-xs text-white/60 px-1 mt-1" data-testid="onboard-lifestyle-hint">
                    ℹ {t.onboarding.profile_lifestyle_hint}
                  </div>
                )}
              </div>
            </Field>

            {profile.profile_type === "pro" && !serviceMode && (
              <div
                className="px-3 py-2 font-mono text-xs tracking-wider"
                style={{ background: "rgba(234,179,8,0.08)", border: "1px solid #eab308", color: "#eab308" }}
                data-testid="onboard-pro-lock-warning"
              >
                🔒 {t.onboarding.pro_lock_warning}
              </div>
            )}
            {serviceMode && (
              <div
                className="px-3 py-2 font-mono text-xs tracking-wider"
                style={{ background: "rgba(34,197,94,0.08)", border: "1px solid #22c55e", color: "#22c55e" }}
                data-testid="onboard-service-mode-badge"
              >
                🛠 SERVICE MODE — fără restricții, fără consum de cotă, fără auto-restaurare.
              </div>
            )}

            {identityLock && (
              <div
                data-testid="identity-lock-notice"
                className="p-3 text-sm mb-2"
                style={{ background: "#1A1410", border: "1px solid #4A3520",
                         borderLeft: "3px solid #F59E0B", color: "#FFE0B5" }}
              >
                <strong>
                  {lang === "ro" ? "Identitate blocată" : "Identity locked"}:
                </strong>{" "}
                {lang === "ro"
                  ? "Numele, sexul, vârsta, sportul și postul sunt fixate la prima utilizare a codului tău. Poți schimba doar greutate, înălțime, obiectiv și volum antrenament."
                  : "Name, sex, age, sport and position are fixed at the first use of your access code. You can still change weight, height, goal and training volume."}
              </div>
            )}

            <Field label={t.onboarding.name}>
              <input
                type="text"
                maxLength="40"
                data-testid="onboard-name"
                className="sne-input"
                disabled={isLocked("name")}
                value={profile.name}
                placeholder={isLifestyle ? (t.onboarding.name_placeholder_lifestyle || t.onboarding.name_placeholder) : t.onboarding.name_placeholder}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label={t.onboarding.sex}>
              <div className="flex gap-2">
                {["M", "F"].map((sx) => (
                  <button
                    key={sx}
                    data-testid={`onboard-sex-${sx}`}
                    onClick={() => !isLocked("sex") && set("sex", sx)}
                    disabled={isLocked("sex")}
                    className="flex-1 py-3 font-mono text-xs tracking-widest"
                    style={{
                      background: profile.sex === sx ? "#FF3B30" : "transparent",
                      color: "white",
                      border: `1px solid ${profile.sex === sx ? "#FF3B30" : "#27272A"}`,
                      opacity: isLocked("sex") && profile.sex !== sx ? 0.4 : 1,
                      cursor: isLocked("sex") ? "not-allowed" : "pointer",
                    }}
                  >
                    {sx === "M" ? t.onboarding.sex_m : t.onboarding.sex_f}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t.onboarding.age}>
              <input type="number" min="10" max="80" data-testid="onboard-age"
                     className="sne-input" value={profile.age || ""}
                     disabled={isLocked("age")}
                     onFocus={(e) => e.target.select()}
                     onChange={(e) => set("age", parseInt(e.target.value) || 0)} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t.onboarding.weight}>
                <input type="number" min="30" max="200" step="0.1" data-testid="onboard-weight"
                       className="sne-input" value={profile.weight_kg || ""}
                       onFocus={(e) => e.target.select()}
                       onChange={(e) => set("weight_kg", parseFloat(e.target.value) || 0)} />
              </Field>
              <Field label={t.onboarding.height}>
                <input type="number" min="120" max="230" data-testid="onboard-height"
                       className="sne-input" value={profile.height_cm || ""}
                       onFocus={(e) => e.target.select()}
                       onChange={(e) => set("height_cm", parseFloat(e.target.value) || 0)} />
              </Field>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-5">
            {!isLifestyle && (
              <Field label={t.onboarding.sport}>
                <select className="sne-select" data-testid="onboard-sport"
                        disabled={isLocked("sport")}
                        value={profile.sport}
                        onChange={(e) => {
                          const sp = sports.find((s) => s.key === e.target.value);
                          set("sport", e.target.value);
                          if (sp?.positions?.length) set("position", sp.positions[0].key);
                        }}>
                  {filteredSports.map((s) => (
                    <option key={s.key} value={s.key}>{s[`label_${lang}`] || s.label_en}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label={isLifestyle ? (t.onboarding.activity_level_lifestyle || t.onboarding.activity_level) : t.onboarding.position}>
              <select className="sne-select" data-testid="onboard-position"
                      disabled={!isLifestyle && isLocked("position")}
                      value={profile.position}
                      onChange={(e) => {
                        const newPos = e.target.value;
                        set("position", newPos);
                        if (isLifestyle) {
                          set("activity_level", LIFESTYLE_POS_TO_ACTIVITY[newPos] || "moderate");
                        }
                      }}>
                {currentSport?.positions?.map((p) => (
                  <option key={p.key} value={p.key}>{p[`label_${lang}`] || p.label_en}</option>
                ))}
              </select>
            </Field>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-5">
            {!isLifestyle && (
              <Field label={t.onboarding.activity_level}>
                <select className="sne-select" data-testid="onboard-activity"
                        value={profile.activity_level}
                        onChange={(e) => set("activity_level", e.target.value)}>
                  {Object.entries(t.onboarding.activity).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label={t.onboarding.goal}>
              <select className="sne-select" data-testid="onboard-goal"
                      value={profile.goal}
                      onChange={(e) => set("goal", e.target.value)}>
                {Object.entries(isLifestyle ? t.onboarding.goals_lifestyle : t.onboarding.goals).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </Field>

            {/* Target weight + timeline — only when goal involves transformation.
                "performance" and "maintain" do not need a target weight. */}
            {["cut", "weight_loss", "bulk", "muscle_gain", "recomposition"].includes(profile.goal) && (
              <TargetWeightFields profile={profile} set={set} lang={lang} />
            )}
          </div>
        )}
        {step === 4 && (
          <div className="space-y-5">
            <Field label={t.onboarding.special}>
              <div className="flex flex-wrap gap-2">
                {Object.entries(t.onboarding.special_opts).map(([k, v]) => (
                  <button
                    key={k}
                    data-testid={`onboard-special-${k}`}
                    onClick={() => {
                      const has = profile.special_conditions.includes(k);
                      set("special_conditions", has
                        ? profile.special_conditions.filter((x) => x !== k)
                        : [...profile.special_conditions, k]);
                    }}
                    className="px-3 py-2 font-mono text-xs tracking-widest"
                    style={{
                      background: profile.special_conditions.includes(k) ? "#FF3B30" : "transparent",
                      color: "white",
                      border: `1px solid ${profile.special_conditions.includes(k) ? "#FF3B30" : "#27272A"}`,
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        <div className="flex justify-between mt-8">
          {canGoBack ? (
            <button onClick={back} data-testid="onboard-back" className="btn-secondary">
              ← {t.onboarding.back}
            </button>
          ) : (
            <div />
          )}
          {step < 4 ? (
            <button onClick={next} data-testid="onboard-next" className="btn-primary">
              {t.onboarding.next} →
            </button>
          ) : (
            <button onClick={finish} disabled={busy} data-testid="onboard-finish" className="btn-primary">
              {busy ? t.common.loading : `${t.onboarding.finish} →`}
            </button>
          )}
        </div>
        {error && (
          <div className="mt-4 p-3 text-sm text-white" data-testid="onboard-error"
               style={{ background: "#2A0F0F", border: "1px solid #FF3B30" }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="sne-label">{label}</label>
    {children}
  </div>
);

/** Target weight + timeline picker. Hits the backend timeline-check endpoint
 *  to surface a medical-safety warning when the chosen months are too short
 *  for the chosen delta (e.g., "98 kg → 70 kg in 3 months" → refused). */
const TargetWeightFields = ({ profile, set, lang }) => {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    if (!profile.target_weight_kg || !profile.target_months) {
      setCheck(null);
      return;
    }
    api.post("/plan/timeline-check", profile)
      .then((r) => setCheck(r.data))
      .catch(() => setCheck(null));
  }, [profile.target_weight_kg, profile.target_months, profile.weight_kg,
      profile.age, profile.sex, profile.height_cm, profile.goal, profile]);

  const monthsOptions = [3, 6, 9, 12];

  return (
    <>
      <Field label={lang === "ro" ? "Greutate țintă (kg)" : "Target weight (kg)"}>
        <input
          type="number" min="30" max="200" step="0.5"
          className="sne-input"
          data-testid="onboard-target-weight"
          value={profile.target_weight_kg ?? ""}
          onChange={(e) => set("target_weight_kg", e.target.value ? parseFloat(e.target.value) : null)}
        />
      </Field>
      <Field label={lang === "ro" ? "În câte luni vrei să ajungi acolo?" : "How many months to reach the goal?"}>
        <select
          className="sne-select"
          data-testid="onboard-target-months"
          value={profile.target_months ?? ""}
          onChange={(e) => set("target_months", e.target.value ? parseInt(e.target.value, 10) : null)}
        >
          <option value="">{lang === "ro" ? "Alege..." : "Pick one..."}</option>
          {monthsOptions.map((m) => (
            <option key={m} value={m}>{m} {lang === "ro" ? "luni" : "months"}</option>
          ))}
        </select>
      </Field>
      {check && !check.safe && !check.skipped && (
        <div
          data-testid="timeline-warning"
          className="p-4 text-sm"
          style={{
            background: check.needs_medical_supervision ? "#3A0808" : "#2A0E0E",
            border: `1px solid ${check.needs_medical_supervision ? "#FF1717" : "#FF3B30"}`,
            borderLeft: `3px solid ${check.needs_medical_supervision ? "#FF1717" : "#FF3B30"}`,
            color: "#FFD7D5",
          }}
        >
          <strong>
            {check.needs_medical_supervision
              ? (lang === "ro" ? "SUPRAVEGHERE MEDICALĂ OBLIGATORIE" : "MEDICAL SUPERVISION OBLIGATORY")
              : (lang === "ro" ? "Atenție medicală" : "Medical warning")}:
          </strong>
          <div
            className="mt-2 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: lang === "ro" ? check.message_ro : check.message_en,
            }}
          />
          <div className="mt-3 font-mono text-[11px] text-white/60 border-t border-white/10 pt-2">
            TDEE: {check.tdee} kcal · {lang === "ro" ? "deficit/zi" : "deficit/day"}: {check.deficit_per_day} kcal
            {" · "}{lang === "ro" ? "rezultă" : "result"}: {check.target_kcal_after_deficit} kcal/
            {lang === "ro" ? "zi" : "day"}
            {check.km_per_day_compensatory > 0 && (
              <> · {lang === "ro" ? "mers compensator" : "compensatory walking"}:{" "}
                <span style={{ color: "#F59E0B" }}>{check.km_per_day_compensatory} km/{lang === "ro" ? "zi" : "day"}</span>
              </>
            )}
          </div>
          {check.sources_ro && (
            <div className="mt-2 font-mono text-[9px] text-white/30 italic">
              {lang === "ro" ? check.sources_ro : check.sources_en}
            </div>
          )}
        </div>
      )}
      {check && check.safe && !check.skipped && (
        <div
          data-testid="timeline-ok"
          className="p-4 text-sm"
          style={{ background: "#0E2A14", border: "1px solid #2A6F3A",
                   borderLeft: "3px solid #2A6F3A", color: "#D7FFD5" }}
        >
          <strong>{lang === "ro" ? "Plan sigur clinic" : "Clinically safe plan"}:</strong>{" "}
          ~{check.rate_kg_per_week} kg/{lang === "ro" ? "săpt" : "wk"} ({check.pct_per_week}%)
          {" · "}{lang === "ro" ? "deficit" : "deficit"}: {check.deficit_per_day} kcal/
          {lang === "ro" ? "zi" : "day"}
          {" · "}{check.target_kcal_after_deficit} kcal/{lang === "ro" ? "zi" : "day"}
          <div className="mt-2 font-mono text-[9px] text-white/40 italic">
            {lang === "ro" ? check.sources_ro : check.sources_en}
          </div>
        </div>
      )}
    </>
  );
};

export default Onboarding;
